import type { BaseFilterInterface } from '@/core/tables/types'
import type { FormFieldFilterType, FilterServiceInterface, SelectOption, SelectValue } from '@/services/forms/types'
import type { RegisteredServices } from '@/core/container/types'
import type { Operation } from '@/services/types'
import type { FilterFieldType } from '@/services/tables/filters/fields/types'
import type { PromiseOr, TransformerCallback } from '@/types'
import type { IconName } from '@/assets/icons/icons'
import type { BasePaginationUrlParams, PaginationData } from '@/services/tables/pagination/types'
import { FILTERED_VIEW_TYPE_PROP_NAME } from '@/services/domain/filteredViews/const'
import type { CounterInterface } from '@/services/tables/types'

export type FilterToCreateCallbackData<T = SelectValue> = {
  id: string
  [key: string]: any
  options: FilterOptions<T> | FetchOptionsFuncWrapper<T>
}
export type FilterOption<TValue = SelectValue> = SelectOption<TValue>
export type FilterOptions<TValue = SelectValue> = Array<FilterOption<TValue>>
export type FetchOptionsFunc<TValue = SelectValue> = (
  search?: string,
  options?: SelectOption<TValue>[],
  deps?: string[],
) => PromiseOr<SelectOption<TValue>[]>
export type FetchOptionsFuncWrapper<TValue = SelectValue> = () => FetchOptionsFunc<TValue>
export type FilterToCreateCallback<T = SelectValue> = TransformerCallback<FilterToCreateCallbackData<T>>

export type TEntityToText = (entity: Record<string, unknown>) => string

export type FilterToCreate<T extends string = string, R = SelectValue> = {
  icon?: IconName
  disabled?: boolean
  label?: string
  relatedFieldName?: string
  queryName?: string
  availableOperations?: Operation[]
  operation?: Operation
  isOptionalOperation?: boolean
  callback?: FilterToCreateCallback<R>
  readonly?: boolean
  filterType?: FilterFieldType
  isDefault?: boolean
} & ({ name: T; isHidden?: false } | { name: string; isHidden: true })

export type FiltersToCreate<T extends string = string, R = SelectValue> = Array<FilterToCreate<T, R>>
export type BaseFiltersType = BaseFilterInterface & FilterServiceInterface<FormFieldFilterType>

export interface FactoryItem<T> {
  id: RegisteredServices
  instance: T
}

export type ToFilterQueryPart = {
  operation: Operation
  relationField?: string
  value: unknown
}

export type FilterSupportInfo = {
  isSupported: boolean
  availableOperations: Operation[]
}
export type FilterModalParams = {
  name: string
  title: string
  description?: string
  isHidden?: boolean
}

export const ALL_FILTER_OPTION = '[ALL]'
export type StringRange = [string, string]
export type DateRange = [Date, Date]

export type TIsLastPageFunc = (page: number, pagination: PaginationData) => boolean
export type TFetchOptionsFunc<T> = (
  search: string,
  requestParams: BasePaginationUrlParams,
) => Promise<{ items: T[]; pagination: PaginationData }>
export type TConvertToOptionsFunc<T, R> = (items: T[]) => FilterOptions<R>
export type TStandaloneCounterSettings = {
  getCounterService: () => CounterInterface
  counterId: string
}

export const FILTER_RESERVED_NAMES = [FILTERED_VIEW_TYPE_PROP_NAME] as string[]

export const FILTER_RELATION_IS_EMPTY_OPTION_VALUE = 'array-value-is-empty' as const
